import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CalendarDays, ChevronLeft, ChevronRight, Radio } from "lucide-react";
import { useMemo, useState } from "react";

import { LineUp } from "@/components/home/line-up";
import { Image } from "@/components/image";
import { Link } from "@/components/link";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api";
import { lineUpOf } from "@/lib/episodes";
import type { PublicEpisode } from "@/lib/explore";
import { cn, datesAreOnSameDay, formatTimeToHHMM } from "@/lib/utils";

const DAYS_SHOWN = 7;

const dayStart = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const mondayOf = (date: Date) => addDays(date, -((date.getDay() + 6) % 7));

const weekdayLabel = (date: Date) =>
  date.toLocaleDateString("en-GB", { weekday: "short" });

const dayLabel = (date: Date) =>
  date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });

const fullDayLabel = (date: Date) =>
  date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    weekday: "long",
  });

const seasonDayLabel = (date: Date) =>
  date.toLocaleDateString("en-GB", { day: "numeric", month: "long" });

const seasonRangeLabel = (from: Date, to: Date) =>
  `${seasonDayLabel(from)} – ${seasonDayLabel(to)}`;

const useWeekEpisodes = (weekStart: Date) => {
  const weekEnd = addDays(weekStart, DAYS_SHOWN);

  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const query = new URLSearchParams({
        from: weekStart.toISOString(),
        limit: "100",
        to: weekEnd.toISOString(),
      });
      const data = await apiFetch<{ episodes: PublicEpisode[] }>(
        `/api/public/episodes?${query.toString()}`
      );
      return data?.episodes ?? [];
    },
    queryKey: ["episodes", "week", weekStart.toISOString()] as const,
    staleTime: 5 * 60 * 1000,
  });
};

interface PublicSeason {
  current: boolean;
  endsAt: string;
  id: string;
  startsAt: string;
}

const usePublicSeasons = () =>
  useQuery({
    queryFn: async () => {
      const data = await apiFetch<{
        currentSeasonId: string | null;
        seasons: PublicSeason[];
      }>("/api/public/seasons");
      return {
        currentSeasonId: data?.currentSeasonId ?? null,
        seasons: data?.seasons ?? [],
      };
    },
    queryKey: ["seasons", "public"] as const,
    staleTime: 5 * 60 * 1000,
  });

const useSeasonEpisodes = (seasonId: string | null) =>
  useQuery({
    queryFn: async () => {
      const path = seasonId
        ? `/api/public/episodes/current-season?seasonId=${seasonId}`
        : "/api/public/episodes/current-season";
      const data = await apiFetch<{ episodes: PublicEpisode[] }>(path);
      return data?.episodes ?? [];
    },
    queryKey: ["episodes", "season", seasonId] as const,
    staleTime: 5 * 60 * 1000,
  });

function ScheduleRow({ episode }: { episode: PublicEpisode }) {
  const startsAt = new Date(episode.startsAt);
  const finished = new Date(episode.endsAt) < new Date();

  return (
    <li>
      <Link
        className={cn(
          "group flex items-start gap-3 rounded-xl px-2 py-3 sm:items-center transition-colors hover:bg-muted sm:gap-4 sm:px-3",
          finished && "opacity-50"
        )}
        href={`/view/episodes/${episode.id}`}
      >
        <span className="hidden w-12 shrink-0 text-sm text-muted-foreground tabular-nums sm:block">
          {formatTimeToHHMM(startsAt)}
        </span>
        <span className="relative block size-10 shrink-0 overflow-hidden rounded-lg bg-muted">
          {episode.iconUrl ? (
            <Image
              fill
              alt={`${episode.showName} artwork`}
              className="object-cover"
              src={episode.iconUrl}
            />
          ) : (
            <span className="text-primary flex size-full items-center justify-center">
              <Radio className="size-5" />
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-muted-foreground tabular-nums sm:hidden">
            {formatTimeToHHMM(startsAt)}
          </span>
          <span
            className={cn(
              "block truncate font-semibold group-hover:underline",
              episode.cancelled && "line-through"
            )}
          >
            {episode.title}
          </span>
          <span className="block truncate text-sm text-muted-foreground">
            {episode.showName}
          </span>
          <LineUp
            className="text-muted-foreground"
            {...lineUpOf(episode.participants)}
          />
        </span>
      </Link>
    </li>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-xl bg-muted/60 px-5 py-6 text-sm text-muted-foreground">
      {children}
    </p>
  );
}

function WeekNavigator(props: {
  days: Date[];
  onNext: () => void;
  onPrevious: () => void;
  onToday: () => void;
  weekOffset: number;
}) {
  const [first] = props.days;
  const last = props.days.at(-1) ?? first;
  const relativeLabel =
    {
      [-1]: "Last week",
      0: "This week",
      1: "Next week",
    }[props.weekOffset] ?? null;

  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold">
          {dayLabel(first)} – {dayLabel(last)}
        </p>
        {relativeLabel && (
          <p className="text-xs text-muted-foreground">{relativeLabel}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {props.weekOffset !== 0 && (
          <Button onClick={props.onToday} size="sm" variant="ghost">
            Today
          </Button>
        )}
        <Button
          aria-label="Previous week"
          onClick={props.onPrevious}
          size="icon-sm"
          variant="outline"
        >
          <ChevronLeft />
        </Button>
        <Button
          aria-label="Next week"
          onClick={props.onNext}
          size="icon-sm"
          variant="outline"
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

function WeekView({ onShowSeason }: { onShowSeason: () => void }) {
  const today = dayStart(new Date());
  const [weekOffset, setWeekOffset] = useState(0);
  const [chosenDay, setChosenDay] = useState<Date | null>(null);
  const weekStart = addDays(mondayOf(today), weekOffset * DAYS_SHOWN);
  const {
    data: episodes,
    isPending,
    isError,
    refetch,
  } = useWeekEpisodes(weekStart);
  const weekEndTime = addDays(weekStart, DAYS_SHOWN).getTime();
  const sortedEpisodes = (episodes ?? [])
    .filter((episode) => {
      const startsAt = Date.parse(episode.startsAt);
      return startsAt >= weekStart.getTime() && startsAt < weekEndTime;
    })
    .toSorted((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const firstUpcoming = sortedEpisodes.find(
    (episode) => !episode.cancelled && new Date(episode.endsAt) > new Date()
  );
  const fallbackDay = weekOffset === 0 ? today : weekStart;
  const focusEpisode =
    weekOffset === 0 ? firstUpcoming : (firstUpcoming ?? sortedEpisodes[0]);
  const selectedDay =
    chosenDay ??
    (focusEpisode ? dayStart(new Date(focusEpisode.startsAt)) : fallbackDay);

  const weekStartTime = weekStart.getTime();
  const days = useMemo(
    () =>
      Array.from({ length: DAYS_SHOWN }, (_, index) =>
        addDays(new Date(weekStartTime), index)
      ),
    [weekStartTime]
  );

  const dayEpisodes = sortedEpisodes.filter((episode) =>
    datesAreOnSameDay(new Date(episode.startsAt), selectedDay)
  );

  const moveWeek = (offset: number) => {
    setWeekOffset(offset);
    setChosenDay(null);
  };

  const navigator = (
    <WeekNavigator
      days={days}
      onNext={() => moveWeek(weekOffset + 1)}
      onPrevious={() => moveWeek(weekOffset - 1)}
      onToday={() => moveWeek(0)}
      weekOffset={weekOffset}
    />
  );

  if (isPending) {
    return (
      <div>
        {navigator}
        <EmptyState>Loading the schedule…</EmptyState>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <EmptyState>We couldn&apos;t load the schedule.</EmptyState>
        <Button
          onClick={() => {
            refetch();
          }}
          variant="outline"
          size="sm"
        >
          Try again
        </Button>
      </div>
    );
  }

  if (sortedEpisodes.length === 0) {
    return (
      <div>
        {navigator}
        <div className="flex flex-col gap-5 rounded-2xl bg-muted/60 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div className="max-w-lg">
            <h3 className="font-semibold">A quiet week on air</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              No shows are scheduled this week. Look ahead, browse the full
              season or catch up on a recording.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-3">
            <Button
              onClick={() => moveWeek(weekOffset + 1)}
              variant="outline"
              size="sm"
            >
              Next week
            </Button>
            <Button onClick={onShowSeason} variant="outline" size="sm">
              Whole season
            </Button>
            <Link
              className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
              href="/explore"
            >
              Browse recordings
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {navigator}
      <div className="grid grid-cols-7 gap-1 rounded-2xl bg-muted/60 p-1.5 sm:gap-2">
        {days.map((day) => {
          const selected = datesAreOnSameDay(day, selectedDay);
          const isToday = datesAreOnSameDay(day, today);
          const count = sortedEpisodes.filter((episode) =>
            datesAreOnSameDay(new Date(episode.startsAt), day)
          ).length;

          return (
            <button
              className={cn(
                "flex min-w-0 flex-col items-center gap-1 rounded-xl border border-transparent px-1 py-3 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary",
                selected
                  ? "border-primary bg-primary text-primary-foreground"
                  : "hover:border-border hover:bg-background",
                !selected && day < today && "opacity-60"
              )}
              aria-current={isToday ? "date" : undefined}
              aria-pressed={selected}
              aria-label={`${fullDayLabel(day)}, ${count} ${count === 1 ? "show" : "shows"}`}
              key={day.toISOString()}
              onClick={() => setChosenDay(day)}
              type="button"
            >
              <span className="text-[0.65rem] font-medium uppercase tracking-wide opacity-70 sm:text-xs">
                {isToday ? "Today" : weekdayLabel(day)}
              </span>
              <span className="text-xs font-semibold sm:text-sm">
                {dayLabel(day)}
              </span>
              <span
                className={cn(
                  "hidden text-xs tabular-nums sm:block",
                  selected ? "opacity-80" : "text-muted-foreground"
                )}
              >
                {count > 0
                  ? `${count} ${count === 1 ? "show" : "shows"}`
                  : "No shows"}
              </span>
            </button>
          );
        })}
      </div>

      <h3 className="mt-6 mb-3 text-sm font-semibold">
        {fullDayLabel(selectedDay)}
      </h3>

      {dayEpisodes.length === 0 && (
        <EmptyState>
          No shows on this day. Choose another date or browse the whole season.
        </EmptyState>
      )}
      {dayEpisodes.length > 0 && (
        <ul className="mt-2 divide-y">
          {dayEpisodes.map((episode) => (
            <ScheduleRow episode={episode} key={episode.id} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SeasonView() {
  const seasons = usePublicSeasons();
  const [chosenSeasonId, setChosenSeasonId] = useState<string | null>(null);
  const seasonId =
    chosenSeasonId ??
    seasons.data?.currentSeasonId ??
    seasons.data?.seasons?.[0]?.id ??
    null;
  const {
    data: episodes,
    isPending,
    isError,
    refetch,
  } = useSeasonEpisodes(seasonId);
  const seasonLabel = (season: PublicSeason) =>
    seasonRangeLabel(new Date(season.startsAt), new Date(season.endsAt));

  const byDay = useMemo(() => {
    const groups = new Map<string, { date: Date; episodes: PublicEpisode[] }>();
    for (const episode of episodes ?? []) {
      const date = dayStart(new Date(episode.startsAt));
      const key = date.toISOString();
      const group = groups.get(key) ?? { date, episodes: [] };
      group.episodes.push(episode);
      groups.set(key, group);
    }
    return [...groups.values()].toSorted(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
  }, [episodes]);

  if (isPending) {
    return <EmptyState>Loading the season…</EmptyState>;
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <EmptyState>We couldn&apos;t load the season.</EmptyState>
        <Button
          onClick={() => {
            refetch();
          }}
          variant="outline"
          size="sm"
        >
          Try again
        </Button>
      </div>
    );
  }

  const seasonPicker =
    seasons.data && seasons.data.seasons.length > 1 ? (
      <label className="mb-4 flex flex-col gap-1 text-sm">
        Season
        <select
          className="min-h-11 w-full max-w-xs rounded-lg border bg-background px-3"
          onChange={(event) => setChosenSeasonId(event.target.value)}
          value={seasonId ?? ""}
        >
          {seasons.data.seasons.map((season) => (
            <option key={season.id} value={season.id}>
              {seasonLabel(season)}
              {season.current ? " (current)" : ""}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  if (byDay.length === 0) {
    return (
      <div>
        {seasonPicker}
        <EmptyState>
          The next broadcast season hasn&apos;t been scheduled yet. Check back
          soon, or{" "}
          <Link
            className="font-medium text-primary underline underline-offset-4"
            href="/explore"
          >
            browse recordings
          </Link>{" "}
          from past shows.
        </EmptyState>
      </div>
    );
  }

  const first = byDay[0].date;
  const last = byDay.at(-1)?.date ?? first;

  return (
    <div>
      {seasonPicker}
      <p className="text-sm text-muted-foreground">
        {seasonRangeLabel(first, last)}. {episodes?.length} episodes across{" "}
        {byDay.length} broadcast days.
      </p>

      <div className="mt-4 max-h-[32rem] overflow-y-auto rounded-2xl border">
        {byDay.map((group) => (
          <div key={group.date.toISOString()}>
            <h3 className="sticky top-0 z-10 border-b bg-muted px-4 py-3 text-sm font-semibold">
              {fullDayLabel(group.date)}
            </h3>
            <ul className="divide-y px-1 py-1">
              {group.episodes.map((episode) => (
                <ScheduleRow episode={episode} key={episode.id} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScheduleSection() {
  const [view, setView] = useState<"season" | "week">("week");

  return (
    <section
      aria-labelledby="schedule-heading"
      className="mx-auto w-full max-w-7xl scroll-mt-24 px-6 pt-12 lg:px-10"
      id="schedule"
    >
      <div className="overflow-hidden rounded-3xl border border-border/70 bg-background">
        <div className="flex flex-col gap-5 border-b border-border/70 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <h2
              id="schedule-heading"
              className="flex items-center gap-2.5 text-2xl font-bold tracking-tight"
            >
              <CalendarDays
                aria-hidden="true"
                className="text-primary size-5"
              />{" "}
              Schedule
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Your guide to what&apos;s on OSCAR.
            </p>
          </div>
          <div className="flex w-fit shrink-0 gap-1 rounded-full bg-muted p-1">
            <Button
              onClick={() => setView("week")}
              aria-pressed={view === "week"}
              className={cn(
                "px-4",
                view === "week" && "bg-background shadow-sm"
              )}
              size="sm"
              variant="ghost"
            >
              Week
            </Button>
            <Button
              onClick={() => setView("season")}
              aria-pressed={view === "season"}
              className={cn(
                "px-4",
                view === "season" && "bg-background shadow-sm"
              )}
              size="sm"
              variant="ghost"
            >
              Whole season
            </Button>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          {view === "week" ? (
            <WeekView onShowSeason={() => setView("season")} />
          ) : (
            <SeasonView />
          )}
        </div>
      </div>
    </section>
  );
}
